import React, { useState, useEffect } from "react";
import "./CryptoQRModal.css";

import walletIcon from "./../../../../assets/svg/wallet.svg";
import closeIcon from "./../../../../assets/svg/close.svg";

interface CryptoQRModalProps {
  isOpen: boolean;
  onClose: () => void;
  currency: string;
  network: string;
  amount: number;
  qrCodeUrl?: string;
  walletAddress?: string;
}

export const CryptoQRModal: React.FC<CryptoQRModalProps> = ({
  isOpen,
  onClose,
  currency,
  network,
  amount,
  qrCodeUrl,
  walletAddress,
}) => {
  const [copied, setCopied] = useState(false);

  // Закрытие модального окна при клике на фон
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Копирование адреса кошелька
  const handleCopyAddress = () => {
    if (walletAddress) {
      navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  useEffect(() => {
    setCopied(false);
  }, [walletAddress]);

  const [visible, setVisible] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
    } else {
      const timer = setTimeout(() => setVisible(false), 250);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!visible) return null;

  return (
    <div className={`crypto-qr-modal-overlay${!isOpen ? " closing" : ""}`} onClick={handleBackdropClick}>
      <div className={`crypto-qr-modal${!isOpen ? " closing" : ""}`}>
        <div className="crypto-qr-modal-header">
          <img
            className="crypto-qr-modal-logo"
            src={walletIcon}
            alt="Wallet logo"
          />
          <h2 className="crypto-qr-modal-title">
            Deposit {currency.toUpperCase()}
          </h2>
          <button className="close-button" onClick={onClose}>
            <img src={closeIcon} alt="Close icon" />
            Close
          </button>
        </div>

        <div className="crypto-qr-content">
          {/* Информация о депозите */}
          <div className="deposit-info">
            <div className="info-row">
              <span className="info-label">Currency:</span>
              <span className="info-value">{currency.toUpperCase()}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Network:</span>
              <span className="info-value">{network.toUpperCase()}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Amount:</span>
              <span className="info-value highlight">
                ${amount.toLocaleString()}
              </span>
            </div>
          </div>

          {/* QR код */}
          <div className="qr-code-container">
            {qrCodeUrl ? (
              <img src={qrCodeUrl} alt="QR Code" className="qr-code" />
            ) : (
              <div className="qr-placeholder">
                <div className="qr-loading">
                  <div className="spinner"></div>
                  <p>Generating QR Code...</p>
                </div>
              </div>
            )}
          </div>

          {/* Адрес кошелька */}
          {walletAddress && (
            <div className="wallet-address-section">
              <label>Wallet Address:</label>
              <div className="wallet-address-container">
                <input
                  type="text"
                  value={walletAddress}
                  readOnly
                  className="wallet-address-input"
                />
                <button
                  type="button"
                  className="copy-button"
                  onClick={handleCopyAddress}
                >
                  {copied ? "✓ Copied" : "📋 Copy"}
                </button>
              </div>
            </div>
          )}

          {/* Инструкция */}
          <div className="instructions">
            <h3>Instructions:</h3>
            <ol>
              <li>Scan the QR code with your crypto wallet app</li>
              <li>Or copy the wallet address above</li>
              <li>
                Send exactly <strong>${amount.toLocaleString()}</strong> worth
                of {currency.toUpperCase()}
              </li>
              <li>Your deposit will be credited after network confirmation</li>
            </ol>
          </div>

          {/* Предупреждение */}
          <div className="warning">
            <strong>⚠️ Important:</strong> Only send {currency.toUpperCase()} on
            the {network.toUpperCase()} network. Sending any other
            cryptocurrency or using a different network may result in permanent
            loss of funds.
          </div>

          {/* Кнопка закрытия */}
          <button className="done-button" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
